/**
 * DevotedSkillEffect — Conjuration Bind path passive: intercept damage
 * reduction and familiar proximity regeneration.
 *
 * Always-on passive. Two complementary effects:
 *   - Intercept damage reduction: when the familiar intercepts a hit for the
 *     player, multiply the incoming damage by (1 - interceptDamageReduction).
 *     With base 0.35, the familiar absorbs only 65% of the intercepted hit.
 *   - Proximity regen: familiar regenerates regenPerSecond × maxHp each
 *     second while the player and familiar occupy the same room.
 *
 * Both values are resolved through UpgradeEffectResolver ('bind' scope),
 * additive so future upgrades stack by summing.
 *
 * Integration hooks:
 *   - `getInterceptDamageMultiplier()` — returns (1 - resolved reduction).
 *     The companion's intercept handler multiplies incoming damage by this.
 *   - `computeRegenHeal(maxHp, deltaMs, sameRoom)` — returns HP to heal
 *     this tick (0 if not in the same room or already at max).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Devoted
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DevotedDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface DevotedConfig {
  /** Base intercept damage reduction fraction (0–1). */
  readonly interceptDamageReduction: number;
  /** Base regen rate as fraction of maxHp per second. */
  readonly regenPerSecond: number;
}

const DEFAULT_CONFIG: DevotedConfig = {
  interceptDamageReduction: 0.35,  // spec § Devoted: 35% damage reduction
  regenPerSecond: 0.03,            // spec § Devoted: 3% maxHp / second
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DevotedSkillEffect implements SkillEffect {
  readonly skillId = 'bind_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: DevotedDeps;
  private readonly config: DevotedConfig;

  // Observable state for scenarios
  private _totalHealed = 0;
  private _interceptsApplied = 0;

  constructor(deps: DevotedDeps, config: Partial<DevotedConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Devoted-specific API ---

  /**
   * Resolve the intercept damage multiplier. When the familiar intercepts a
   * hit, multiply the raw damage by this value to get the HP actually lost.
   *
   * With base 0.35 reduction and no bonuses: returns 0.65.
   * Additive resolver stacking: if a future upgrade adds 0.15, returns 0.50.
   * Clamped so the multiplier never goes below 0 (familiar can't heal from
   * being hit) or above 1 (no worse than no reduction).
   */
  getInterceptDamageMultiplier(): number {
    const bonus = this.deps.resolver.getAdditiveEffect('bind_path_passive', 'interceptDamageReduction');
    const totalReduction = this.config.interceptDamageReduction + bonus;
    return Math.max(0, Math.min(1, 1 - totalReduction));
  }

  /**
   * Apply intercept damage reduction to a raw damage value.
   *
   * Returns the reduced damage the familiar actually takes. Convenience
   * wrapper around getInterceptDamageMultiplier() for integration layer use.
   */
  applyInterceptReduction(rawDamage: number): number {
    const reduced = rawDamage * this.getInterceptDamageMultiplier();
    this._interceptsApplied++;
    return reduced;
  }

  /**
   * Compute how much HP the familiar should regenerate this tick.
   *
   * Returns 0 if the familiar and player are in different rooms (sameRoom
   * is false). The caller is responsible for clamping against maxHp.
   *
   * @param maxHp       Familiar's maximum HP
   * @param currentHp   Familiar's current HP (used to clamp so regen doesn't overheal)
   * @param deltaMs     Elapsed time this tick in milliseconds
   * @param sameRoom    Whether the player and familiar are in the same room
   */
  computeRegenHeal(maxHp: number, currentHp: number, deltaMs: number, sameRoom: boolean): number {
    if (!sameRoom) return 0;
    if (currentHp >= maxHp) return 0;

    const bonusRegen = this.deps.resolver.getAdditiveEffect('bind_path_passive', 'regenPerSecond');
    const resolvedRate = this.config.regenPerSecond + bonusRegen;

    const healAmount = maxHp * resolvedRate * (deltaMs / 1000);
    // Clamp: don't overheal
    const clamped = Math.min(healAmount, maxHp - currentHp);
    this._totalHealed += clamped;
    return clamped;
  }

  // --- State getters ---

  get totalHealed(): number { return this._totalHealed; }
  get interceptsApplied(): number { return this._interceptsApplied; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — regen is computed on demand via computeRegenHeal()
  }

  destroy(): void {
    // No resources to release
  }
}
